import React, { useCallback, useEffect, useRef, useState } from 'react';

import { icon_chevron_down } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';
import { Option } from '@gn-audio/jss-core/common/models';

import { useOutsideAlerter } from 'utils/hooks/useOutsideAlerter';

interface JabraDropdownProps {
  selectedOption?: Option;
  options: Option[];
  onOptionSelect: (option: Option) => void;
  dropdownClassName?: string;
  itemsContainerClassName?: string;
  itemClassName?: string;
}

const DropdownItem: React.FC<{
  option: Option;
  onSelect: (option: Option) => void;
  itemClassName: string;
}> = ({ option, onSelect, itemClassName }) => (
  <li
    onClick={() => onSelect(option)}
    className={itemClassName}
    role="option"
    aria-selected="false"
  >
    {option.title}
  </li>
);

const JabraDropdown: React.FC<JabraDropdownProps> = ({
  selectedOption,
  options,
  onOptionSelect,
  dropdownClassName = 'relative flex flex-grow cursor-pointer items-center justify-between',
  itemsContainerClassName = 'absolute  m-auto  top-full z-10 mt-1 min-w-[100%] rounded-md bg-white shadow-lg',
  itemClassName = 'cursor-pointer px-4 py-2 hover:bg-gray-200 whitespace-nowrap',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  const toggleDropdown = () => {
    recalculateOffset();

    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: Option) => {
    onOptionSelect(option);
    setIsOpen(false);
  };

  const closeMenu = useCallback(() => setIsOpen(false), []);
  useOutsideAlerter(dropdownRef, closeMenu, ['dropdown-trigger']);

  const recalculateOffset = useCallback(() => {
    const MARGIN = 30;
    const bodyWidth = document.body.clientWidth;
    const dropdownRefRight = dropdownRef.current?.getBoundingClientRect().right ?? 0;
    const offset = bodyWidth - (dropdownRefRight + MARGIN);
    setOffset((currentOffset) => Math.min(currentOffset + offset, 0));
  }, []);
  useEffect(() => {
    window.addEventListener('resize', recalculateOffset);
    return () => window.removeEventListener('resize', recalculateOffset);
  }, [recalculateOffset]);

  return (
    <div className={dropdownClassName} style={{ overflow: isOpen ? 'visible' : 'hidden' }}>
      <div
        className="dropdown-trigger flex w-full cursor-pointer items-center justify-between font-bold"
        aria-haspopup="listbox"
        onClick={toggleDropdown}
      >
        <span>{selectedOption?.title}</span>
        <JabraIcon
          className={`ml-2 h-5 w-5 transform transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          Icon={icon_chevron_down}
        />
      </div>

      <div
        ref={dropdownRef}
        style={{ transform: `translateX(${offset}px`, visibility: isOpen ? 'visible' : 'hidden' }}
        className={itemsContainerClassName}
        role="listbox"
      >
        <ul>
          {options.map((option) => (
            <DropdownItem
              key={option.title}
              option={option}
              onSelect={handleOptionClick}
              itemClassName={itemClassName}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default JabraDropdown;
